/**
 * MomentTools — the chat composer's tools row on the native-player review:
 * **Capture frame** (the same capture → markup flow as C and the transport)
 * and **Add timestamp** (pins the playhead at the click). The chat renders it
 * above the input and hides it while replying or editing; this component only
 * decides whether each tool is usable right now.
 */
import { Camera, Clock } from '../../../../../design-system/icons';
import { Button } from '../../../../ui/button';
import { CAPTURE_UNAVAILABLE_TITLE, isCaptureUnsupported } from '../lib/captureAvailability';
import type { MediaController } from '../media/useMediaController';
import type { NotesSession } from '../session/useNotesSession';

const CAPTURE_LABEL = 'Capture frame';
const CAPTURE_TITLE = 'Capture & mark up this frame (C)';
const ADD_TIMESTAMP_LABEL = 'Add timestamp';
const ADD_TIMESTAMP_TITLE = 'Pin this moment of the video to your message';
const FRAME_STAGED_TITLE = 'The attached frame already carries its time';
/** A `size-*` class, not a `size` prop: the `xs` Button forces `size-3` on any svg without one. */
const TOOL_ICON_CLASS = 'size-3.5';

/**
 * The slice of the session the tools read. Narrow on purpose: the panel can
 * memoise exactly this slice, so the chat's `richComposer` keeps its identity
 * while nothing the tools show has changed. A whole `NotesSession` fits too.
 */
export type MomentToolsSession = Pick<
  NotesSession,
  'canWrite' | 'isLocked' | 'canCapture' | 'canCaptureNow' | 'staged' | 'requestCapture' | 'addTimestamp'
> & {
  controller: Pick<MediaController, 'ready'>;
};

export interface MomentToolsProps {
  session: MomentToolsSession;
}

function hasStagedFrame(session: MomentToolsSession): boolean {
  return !!session.staged?.frameUrl;
}

function isAddTimestampDisabled(session: MomentToolsSession): boolean {
  return hasStagedFrame(session) || !session.controller.ready;
}

function captureTitle(session: MomentToolsSession): string {
  if (isCaptureUnsupported({ ready: session.controller.ready, canCapture: session.canCapture })) return CAPTURE_UNAVAILABLE_TITLE;
  return CAPTURE_TITLE;
}

function addTimestampTitle(session: MomentToolsSession): string {
  if (hasStagedFrame(session)) return FRAME_STAGED_TITLE;
  return ADD_TIMESTAMP_TITLE;
}

export function MomentTools({ session }: MomentToolsProps) {
  if (!session.canWrite || session.isLocked) return null;

  function handleCapture(): void {
    session.requestCapture();
  }

  function handleAddTimestamp(): void {
    session.addTimestamp();
  }

  return (
    <>
      <Button type="button" variant="outline" size="xs" title={captureTitle(session)} disabled={!session.canCaptureNow} onClick={handleCapture}>
        <Camera className={TOOL_ICON_CLASS} />
        {CAPTURE_LABEL}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="xs"
        title={addTimestampTitle(session)}
        disabled={isAddTimestampDisabled(session)}
        onClick={handleAddTimestamp}
      >
        <Clock className={TOOL_ICON_CLASS} />
        {ADD_TIMESTAMP_LABEL}
      </Button>
    </>
  );
}
